/**
 * Vehicle URL Slug Utilities
 * Generates SEO-friendly URLs for vehicles
 * Format: /vehicle/year-make-model-id
 * Example: /vehicle/2024-ford-f150-30
 */

/**
 * Generate a URL-safe slug from vehicle data
 * @param {Object} vehicle - Vehicle object with year, make, model, id
 * @returns {string} - SEO-friendly slug
 */
export const generateVehicleSlug = (vehicle) => {
  if (!vehicle) return '';
  
  const { year, make, model, id } = vehicle;
  
  // Create slug parts, filtering out empty values
  const parts = [year, make, model]
    .filter(Boolean)
    .map(part => 
      String(part)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
        .replace(/^-+|-+$/g, '')      // Remove leading/trailing hyphens
    )
    .filter(part => part.length > 0);
  
  // Always append the ID at the end for reliable lookup
  parts.push(id);
  
  return parts.join('-');
};

/**
 * Extract vehicle ID from a slug or raw ID
 * Supports both formats:
 * - Old: "30" (just ID)
 * - New: "2024-ford-f150-30" (slug with ID at end)
 * @param {string} slugOrId - The URL parameter
 * @returns {string} - The vehicle ID
 */
export const extractVehicleId = (slugOrId) => {
  if (!slugOrId) return null;
  
  // If it's just a number, return it directly
  if (/^\d+$/.test(slugOrId)) {
    return slugOrId;
  }
  
  // Otherwise, extract the ID from the end of the slug
  // The ID is always the last segment after the final hyphen
  const parts = slugOrId.split('-');
  const lastPart = parts[parts.length - 1];
  
  // Verify it's a number
  if (/^\d+$/.test(lastPart)) {
    return lastPart;
  }
  
  // Fallback: try to find any number in the slug
  const numbers = slugOrId.match(/\d+/g);
  if (numbers && numbers.length > 0) {
    return numbers[numbers.length - 1];
  }
  
  return slugOrId; // Return as-is if we can't extract an ID
};

/**
 * Generate the full vehicle URL path
 * @param {Object} vehicle - Vehicle object
 * @returns {string} - Full URL path like /vehicle/2024-ford-f150-30
 */
export const getVehicleUrl = (vehicle) => {
  const slug = generateVehicleSlug(vehicle);
  return `/vehicle/${slug}`;
};

/**
 * Check if a slug matches a vehicle (for validation)
 * @param {string} slug - URL slug
 * @param {Object} vehicle - Vehicle object
 * @returns {boolean}
 */
export const slugMatchesVehicle = (slug, vehicle) => {
  const expectedSlug = generateVehicleSlug(vehicle);
  return slug === expectedSlug || slug === String(vehicle.id);
};

export default {
  generateVehicleSlug,
  extractVehicleId,
  getVehicleUrl,
  slugMatchesVehicle
};
